import { checkSession } from "@features/auth";
import { getUserOrganizationId } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { EmptyCard3 } from "@shadcn/pro-blocks/application/empty-sections/empty-section-4";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Lock, Navigation, ReceiptText, Ruler, Share } from "lucide-react";
import { fetchQuery, graphql } from "relay-runtime";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { lockOnboardingGuardQuery } from "./__generated__/lockOnboardingGuardQuery.graphql";

// Entry guard for the lock section:
// - If the user holds an active lock at THIS hub, go straight to "my locks".
// - Otherwise, if they have ever used bace lock at all, skip the "how it works"
//   onboarding and go to the rent screen.
// - Otherwise, show the onboarding intro.
const onboardingGuardQuery = graphql`
	query lockOnboardingGuardQuery($hubName: String!, $skipOwnedReservations: Boolean!) {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			hub @required(action: THROW) {
				id
			}
		}
		myUtilizationGrants(input: {}, pagination: { page: 0, pageSize: 1000 }) {
			totalCount
			nodes {
				id
				deactivatedAt
				reservation {
					id
					deactivatedAt
					flow
					subtype {
						__typename
					}
					space {
						id
						hub {
							id
						}
					}
				}
			}
		}
		lockReservations(filter: { subtype: B2B, flow: SWAP }, pagination: { page: 0, pageSize: 1000 }) @skip(if: $skipOwnedReservations) {
			totalCount
			nodes {
				id
				deactivatedAt
				space {
					id
					hub {
						id
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/swap/")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		// Onboarding is for signed-in accounts only; if there's no session the
		// user hasn't used lock yet, so just show the intro.
		const sessionExists = await checkSession();
		if (!sessionExists) return;
		const hasOrganization = Boolean(getUserOrganizationId());

		const data = await fetchQuery<lockOnboardingGuardQuery>(ctx.context.relayEnvironment, onboardingGuardQuery, {
			hubName: ctx.params.hubMetaName,
			skipOwnedReservations: !hasOrganization,
		}).toPromise();

		// Already holds an active lock at this hub → go straight to "my locks".
		const hasActiveGrantHere = data?.myUtilizationGrants.nodes.some(
			(grant) =>
				grant.reservation.subtype.__typename === "B2BlockReservationSubtype" &&
				grant.reservation.flow === "SWAP" &&
				!grant.deactivatedAt &&
				!grant.reservation.deactivatedAt &&
				grant.reservation.space.hub.id === data.hubMeta.hub.id
		);
		const hasActiveOwnedLockHere = data?.lockReservations?.nodes.some(
			(reservation) => !reservation.deactivatedAt && reservation.space.hub.id === data.hubMeta.hub.id
		);
		const hasActiveLockHere = hasActiveGrantHere || hasActiveOwnedLockHere;
		if (hasActiveLockHere) {
			throw redirect({
				to: "/$hubMetaName/swap/my",
				params: { hubMetaName: ctx.params.hubMetaName },
			});
		}
		if (!hasOrganization) {
			throw redirect({ to: "/" });
		}

		// Has used bace lock before (anywhere) → skip onboarding, go to rent.
		const hasUsedLock =
			(data?.myUtilizationGrants.nodes.some(
				(grant) => grant.reservation.subtype.__typename === "B2BlockReservationSubtype" && grant.reservation.flow === "SWAP"
			) ??
				false) ||
			(data?.lockReservations?.totalCount ?? 0) > 0;
		if (hasUsedLock) {
			throw redirect({
				to: "/$hubMetaName/swap/select",
				params: { hubMetaName: ctx.params.hubMetaName },
			});
		}
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const { hubMetaName } = Route.useParams();
	const i18n = useI18nContext().LL["new-ui"];

	const steps = [
		{
			icon: Navigation,
			title: i18n.LOCK_ONBOARDING_GO_TO_HUB(),
			description: i18n.LOCK_ONBOARDING_GO_TO_HUB_DESC(),
		},
		{
			icon: Ruler,
			title: i18n.LOCK_ONBOARDING_SELECT_SIZE(),
			description: i18n.LOCK_ONBOARDING_SELECT_SIZE_DESC(),
		},
		{
			icon: ReceiptText,
			title: i18n.LOCK_ONBOARDING_COMPLETE_ORDER(),
			description: i18n.LOCK_ONBOARDING_COMPLETE_ORDER_DESC(),
		},
		{
			icon: Lock,
			title: i18n.LOCK_ONBOARDING_STORE_ITEMS(),
			description: i18n.LOCK_ONBOARDING_STORE_ITEMS_DESC(),
		},
		{
			icon: Share,
			title: i18n.LOCK_ONBOARDING_SHARE_ACCESS(),
			description: i18n.LOCK_ONBOARDING_SHARE_ACCESS_DESC(),
		},
	];

	return (
		<PageContainer
			footer={
				<Button
					className="w-full shadow-xl"
					size="lg"
					onClick={() => navigate({ to: "/$hubMetaName/swap/select", params: { hubMetaName } })}
				>
					<T>{i18n.LOCK_ONBOARDING_CONTINUE()}</T>
				</Button>
			}
		>
			<AppHeader service="LOCK_B2B" />
			<h1 className="px-6 pt-6 text-2xl font-breit font-medium">
				<T>{i18n.LOCK_ONBOARDING_TITLE()}</T>
			</h1>
			<div className="mx-6">
				{steps.map((step) => (
					<EmptyCard3
						key={step.title}
						icon={step.icon}
						title={step.title}
						description={step.description}
						iconContainerClassName="h-12 w-12 rounded-md border shadow-xs"
					/>
				))}
			</div>
		</PageContainer>
	);
}
